import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Search, Users, Cake, Download } from "lucide-react";
import EmployeeProfileModal from "../components/EmployeeProfileModal";
import { exportCsv } from "../lib/exportCsv";

const contractLabels = {
  CLT_FrankCrum: "CLT · FrankCrum",
  PJ_Lummar: "PJ · Lummar",
  PJ_Direct: "PJ · Direct",
};

const MONTH_KEYS = ["dir.month_1", "dir.month_2", "dir.month_3", "dir.month_4", "dir.month_5", "dir.month_6", "dir.month_7", "dir.month_8", "dir.month_9", "dir.month_10", "dir.month_11", "dir.month_12"];

function BirthdayCard({ users, t }) {
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const todayDay = today.getDate();
  const birthdayUsers = users
    .filter((u) => u.birth_date)
    .map((u) => {
      const [_y, m, d] = u.birth_date.split("-").map((x) => parseInt(x));
      return { ...u, _bMonth: m, _bDay: d };
    })
    .filter((u) => u._bMonth === currentMonth)
    .sort((a, b) => a._bDay - b._bDay);

  return (
    <div className="sw-card !p-5" data-testid="birthday-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-[12px] bg-gradient-to-br from-[#475569] to-[#94a3b8] text-white flex items-center justify-center"><Cake size={18} /></div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">{t("dir.birthdays_title")}</div>
          <div className="font-medium text-[16px] text-[#323232]">{t(MONTH_KEYS[currentMonth - 1])} · {birthdayUsers.length} {birthdayUsers.length === 1 ? t("dir.person_one") : t("dir.person_many")}</div>
        </div>
      </div>
      {birthdayUsers.length === 0 ? (
        <div className="text-[13px] text-[#747474] py-4 text-center">{t("dir.no_birthdays")}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {birthdayUsers.map((u) => {
            const isToday = u._bDay === todayDay;
            const initials = u.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
            return (
              <div key={u.id} data-testid={`birthday-${u.id}`} className={`flex items-center gap-3 p-2.5 rounded-[12px] ${isToday ? "bg-gradient-to-r from-[#e2e8f0] to-white border border-[#475569]" : "bg-[#f6f6f6]"}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold shrink-0 ${isToday ? "bg-[#475569] text-white" : "bg-white text-[#475569]"}`}>{initials}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[13px] text-[#323232] truncate">{u.name} {isToday && <span className="text-[10px] text-[#475569]">{t("dir.today_marker")}</span>}</div>
                  <div className="text-[11px] text-[#747474]">{u._bDay}/{currentMonth.toString().padStart(2, "0")} · {u.job_title || u.role}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Directory() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [q, setQ] = useState("");
  const [contract, setContract] = useState("all");
  const [location, setLocation] = useState("all");
  const [scope, setScope] = useState("active"); // active | former
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/departments").then((r) => setDepts(r.data));
  }, []);

  // Privacy: who can see "former team members" and contract_type per row
  const isPrivileged = user && ["hr", "admin", "ceo"].includes(user.role);
  const canSeeContractType = (target) => {
    if (!user) return false;
    if (isPrivileged) return true;
    if (target.id === user.id) return true; // self
    if (target.manager_id === user.id) return true; // direct manager
    return false;
  };
  const canSeeFormerEmployees = isPrivileged || user?.role === "manager";

  const locations = useMemo(() => Array.from(new Set(users.map((u) => u.location).filter(Boolean))), [users]);

  const filtered = users.filter((u) => {
    if (u.role === "admin") return false;
    const isActive = u.active !== false;
    if (scope === "active" && !isActive) return false;
    if (scope === "former" && isActive) return false;
    // Standard employees should never see former colleagues
    if (!isActive && !canSeeFormerEmployees) return false;
    if (contract !== "all" && u.contract_type !== contract) return false;
    if (location !== "all" && u.location !== location) return false;
    if (q && !`${u.name} ${u.email} ${u.job_title || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const formerCount = users.filter((u) => u.role !== "admin" && u.active === false).length;

  const handleExport = () => {
    const rows = filtered.map((u) => {
      const dept = depts.find((d) => d.id === u.department_id);
      const row = {
        Name: u.name,
        Email: u.email,
        Role: u.role,
        "Job title": u.job_title || "",
        Department: dept?.name || "",
        Location: u.location || "",
        Status: u.active === false ? "Former" : "Active",
        "Hire date": u.hire_date ? u.hire_date.slice(0, 10) : "",
      };
      if (canSeeContractType(u)) {
        row["Contract type"] = u.contract_type || "";
      }
      return row;
    });
    const date = new Date().toISOString().slice(0, 10);
    exportCsv(`directory_${scope}_${date}.csv`, rows);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center">
            <Users size={22} />
          </div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("directory.title")}</h1>
            <p className="text-[14px] text-[#747474]">{filtered.length} {t("dir.results")}</p>
          </div>
        </div>
        <button
          data-testid="directory-export-csv"
          onClick={handleExport}
          className="sw-btn-secondary"
        >
          <Download size={14} /> {t("common.export_csv")}
        </button>
      </div>

      {scope === "active" && <BirthdayCard users={users.filter((u) => u.role !== "admin" && u.active !== false)} t={t} />}

      {canSeeFormerEmployees && (
        <div className="inline-flex bg-white border border-[#e2e8f0] rounded-full p-1 w-fit">
          <button
            data-testid="directory-tab-active"
            onClick={() => setScope("active")}
            className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${scope === "active" ? "bg-[#475569] text-white" : "text-[#747474]"}`}
          >
            {t("dir.active_tab")}
          </button>
          <button
            data-testid="directory-tab-former"
            onClick={() => setScope("former")}
            className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${scope === "former" ? "bg-[#475569] text-white" : "text-[#747474]"}`}
          >
            {t("dir.former_tab")} · {formerCount}
          </button>
        </div>
      )}

      <div className="sw-card flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#747474]" />
          <input
            data-testid="directory-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="sw-input pl-11"
            placeholder={t("directory.search")}
          />
        </div>
        <select
          data-testid="directory-contract-filter"
          value={contract}
          onChange={(e) => setContract(e.target.value)}
          className={`sw-input lg:w-[200px] ${!isPrivileged ? "hidden" : ""}`}
        >
          <option value="all">{t("directory.all")} · {t("directory.contract")}</option>
          <option value="CLT_FrankCrum">CLT · FrankCrum</option>
          <option value="PJ_Lummar">PJ · Lummar</option>
          <option value="PJ_Direct">PJ · Direct</option>
        </select>
        <select
          data-testid="directory-location-filter"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="sw-input lg:w-[200px]"
        >
          <option value="all">{t("directory.all")} · {t("directory.location")}</option>
          {locations.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((u) => {
          const dept = depts.find((d) => d.id === u.department_id);
          const initials = u.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
          const isFormer = u.active === false;
          return (
            <div
              key={u.id}
              data-testid={isFormer ? `former-user-${u.id}` : `directory-user-${u.id}`}
              onClick={() => setSelected(u)}
              className={`sw-card flex items-start gap-4 cursor-pointer hover:shadow-md transition-shadow ${isFormer ? "opacity-75" : ""}`}
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center text-[16px] font-semibold shrink-0 ${isFormer ? "bg-[#f1f5f9] text-[#94a3b8]" : "bg-[#e2e8f0] text-[#475569]"}`}>
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[15px] text-[#323232] truncate flex items-center gap-2">
                  {u.name}
                  {isFormer && <span className="sw-badge sw-badge-rejected !text-[10px]">{t("dir.former_badge")}</span>}
                </div>
                <div className="text-[13px] text-[#747474] truncate">{u.job_title || "—"}</div>
                <div className="text-[12px] text-[#475569] mt-1 truncate">{u.email}</div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {dept && <span className="sw-badge sw-badge-neutral">{dept.name}</span>}
                  {u.contract_type && canSeeContractType(u) && <span className="sw-badge sw-badge-gold">{contractLabels[u.contract_type]}</span>}
                  {u.location && <span className="sw-badge sw-badge-neutral">{u.location}</span>}
                  {isFormer && u.hire_date && <span className="sw-badge sw-badge-neutral !text-[10px]">{t("dir.hired_on")} {u.hire_date.slice(0,10)}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <EmployeeProfileModal
          user={selected}
          manager={users.find((u) => u.id === selected.manager_id)}
          department={depts.find((d) => d.id === selected.department_id)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
